/**
 * D&D 5th Edition — GameSystemAdapter Implementation
 *
 * Wraps existing @machina/core 5e data (class-progression, character-types,
 * spell-utils, ai-types) into the pluggable GameSystemAdapter interface.
 *
 * This is the reference implementation that other system plugins can follow.
 */
import { getModifier, ALL_CLASS_NAMES, CLASS_HIT_DIE, CLASS_SAVE_PROFICIENCIES, SUBCLASS_LEVEL, CLASS_CASTER_TYPE, XP_THRESHOLDS, getSpellSlots, } from '@machina/core';
// =============================================================================
// 5e CONSTANTS
// =============================================================================
const DND5E_ABILITIES = [
    { id: 'str', name: 'Strength', abbreviation: 'STR', description: 'Physical power, athletics, melee attacks' },
    { id: 'dex', name: 'Dexterity', abbreviation: 'DEX', description: 'Agility, reflexes, ranged attacks, AC' },
    { id: 'con', name: 'Constitution', abbreviation: 'CON', description: 'Health, stamina, hit points' },
    { id: 'int', name: 'Intelligence', abbreviation: 'INT', description: 'Knowledge, reasoning, arcane magic' },
    { id: 'wis', name: 'Wisdom', abbreviation: 'WIS', description: 'Perception, insight, divine magic' },
    { id: 'cha', name: 'Charisma', abbreviation: 'CHA', description: 'Force of personality, social skills, sorcery' },
];
const DND5E_CONDITIONS = [
    { id: 'blinded', name: 'Blinded', description: 'Cannot see. Auto-fail sight checks. Attacks have disadvantage, attacks against have advantage.', hasLevels: false },
    { id: 'charmed', name: 'Charmed', description: 'Cannot attack or target the charmer with harmful effects. Charmer has advantage on social checks.', hasLevels: false },
    { id: 'deafened', name: 'Deafened', description: 'Cannot hear. Auto-fail hearing checks.', hasLevels: false },
    { id: 'frightened', name: 'Frightened', description: 'Disadvantage on ability checks and attacks while source of fear is in line of sight. Cannot willingly move closer.', hasLevels: false },
    { id: 'grappled', name: 'Grappled', description: 'Speed becomes 0. Ends if grappler is incapacitated or forced apart.', hasLevels: false },
    { id: 'incapacitated', name: 'Incapacitated', description: 'Cannot take actions or reactions.', hasLevels: false },
    { id: 'invisible', name: 'Invisible', description: 'Cannot be seen without magic. Attacks have advantage, attacks against have disadvantage.', hasLevels: false },
    { id: 'paralyzed', name: 'Paralyzed', description: 'Incapacitated, cannot move or speak. Auto-fail STR/DEX saves. Attacks have advantage, melee hits are crits.', hasLevels: false },
    { id: 'petrified', name: 'Petrified', description: 'Turned to stone. Weight x10. Incapacitated. Resistant to all damage. Immune to poison/disease.', hasLevels: false },
    { id: 'poisoned', name: 'Poisoned', description: 'Disadvantage on attack rolls and ability checks.', hasLevels: false },
    { id: 'prone', name: 'Prone', description: 'Disadvantage on attacks. Melee attacks against have advantage, ranged have disadvantage. Must spend half movement to stand.', hasLevels: false },
    { id: 'restrained', name: 'Restrained', description: 'Speed 0. Attacks have disadvantage. Attacks against have advantage. Disadvantage on DEX saves.', hasLevels: false },
    { id: 'stunned', name: 'Stunned', description: 'Incapacitated, cannot move, can only speak falteringly. Auto-fail STR/DEX saves. Attacks against have advantage.', hasLevels: false },
    { id: 'unconscious', name: 'Unconscious', description: 'Incapacitated, cannot move or speak, unaware. Drop everything, fall prone. Auto-fail STR/DEX saves. Attacks have advantage, melee crits.', hasLevels: false },
    { id: 'exhaustion', name: 'Exhaustion', description: 'Cumulative levels of fatigue. Level 6 = death.', hasLevels: true, maxLevel: 6 },
    { id: 'concentrating', name: 'Concentrating', description: 'Maintaining a concentration spell. Taking damage requires CON save (DC 10 or half damage).', hasLevels: false },
    { id: 'bloodied', name: 'Bloodied', description: 'At or below 50% hit points. Not an official condition, but commonly used for narration.', hasLevels: false },
];
const DND5E_CLASSES = ALL_CLASS_NAMES.map(name => ({
    id: name.toLowerCase(),
    name,
    hitDie: CLASS_HIT_DIE[name],
    savingThrows: CLASS_SAVE_PROFICIENCIES[name],
    subclassLevel: SUBCLASS_LEVEL[name],
    spellcasting: CLASS_CASTER_TYPE[name],
    primaryAbility: getPrimaryAbility(name),
}));
function getPrimaryAbility(className) {
    switch (className) {
        case 'Barbarian': return ['str'];
        case 'Bard': return ['cha'];
        case 'Cleric': return ['wis'];
        case 'Druid': return ['wis'];
        case 'Fighter': return ['str', 'dex'];
        case 'Monk': return ['dex', 'wis'];
        case 'Paladin': return ['str', 'cha'];
        case 'Ranger': return ['dex', 'wis'];
        case 'Rogue': return ['dex'];
        case 'Sorcerer': return ['cha'];
        case 'Warlock': return ['cha'];
        case 'Wizard': return ['int'];
        case 'Artificer': return ['int'];
    }
}
// =============================================================================
// RULES DEFINITION
// =============================================================================
export const DND5E_RULES = {
    name: 'D&D 5th Edition',
    shortName: '5e',
    license: 'CC-BY-4.0 (SRD 5.1)',
    publisher: 'Wizards of the Coast',
    abilities: DND5E_ABILITIES,
    abilityModFormula: 'floor((score - 10) / 2)',
    characterTemplate: {
        levelCap: 20,
        usesClasses: true,
        usesRaces: true,
        progressionType: 'xp',
        xpTable: [...XP_THRESHOLDS],
        healthModel: 'hit-points',
        defenseModel: 'armor-class',
    },
    classes: DND5E_CLASSES,
    coreResolution: {
        type: 'd20-roll-high',
        critRange: [20],
        fumbleRange: [1],
    },
    combat: {
        initiativeFormula: '1d20 + dex_mod',
        actionEconomy: [
            { id: 'action', name: 'Action', countPerRound: 1 },
            { id: 'bonus_action', name: 'Bonus Action', countPerRound: 1 },
            { id: 'reaction', name: 'Reaction', countPerRound: 1 },
            { id: 'movement', name: 'Movement', description: 'Move up to speed in feet', countPerRound: 1 },
            { id: 'free', name: 'Free Action', description: 'Object interaction, short communication', countPerRound: 1 },
        ],
        roundDuration: '6 seconds',
        gridBased: true,
        gridUnit: '5ft',
        deathMechanic: 'death-saves',
    },
    conditions: DND5E_CONDITIONS,
    magic: {
        type: 'spell-slots',
        castingAbilities: ['int', 'wis', 'cha'],
        concentrationRule: true,
    },
    encounterBalance: {
        ratingSystem: 'cr',
        description: 'Challenge Rating (CR) measures relative encounter difficulty against a party of 4.',
    },
    dmGuidance: {
        systemBrief: `D&D 5th Edition is a d20-based fantasy TTRPG. Characters have 6 ability scores (STR, DEX, CON, INT, WIS, CHA) with modifiers derived from (score - 10) / 2. Most checks are 1d20 + modifier vs a Difficulty Class (DC). Combat uses initiative order, action economy (action + bonus action + reaction per round), and hit points. Magic users have spell slots that deplete and recover on rests.`,
        combatNorms: 'Combat rounds are 6 seconds. Each creature gets one turn per round with an action, bonus action, reaction, movement, and free object interaction. Attacks roll 1d20 + modifier vs target AC. Damage reduces HP. At 0 HP, characters make death saves (3 successes = stable, 3 failures = death). Natural 20 on an attack is a critical hit (double damage dice).',
        socialNorms: 'Social encounters use Charisma-based skills (Persuasion, Deception, Intimidation, Performance). DCs typically range from 10 (easy) to 25 (nearly impossible). Insight opposes Deception. NPCs have dispositions that affect DC. Roleplaying may grant advantage.',
        toneGuidance: '5e is designed for heroic fantasy. Characters are exceptional from level 1 and become legendary by level 15+. Death is possible but not frequent. The tone supports both serious drama and lighthearted adventure.',
        commonMistakes: [
            'Don\'t forget concentration checks when a spellcaster takes damage (DC 10 or half damage, whichever is higher)',
            'Bonus actions are NOT extra actions — each class/spell specifies what can be done as a bonus action',
            'Opportunity attacks only trigger on leaving melee range, not on moving within it',
            'Short rests are 1 hour, long rests are 8 hours. Many abilities recharge on short rest.',
            'Proficiency bonus is level-based (+2 at 1st through +6 at 17th), not class-based',
            'Advantage/disadvantage do NOT stack — multiple sources still just roll 2d20',
        ],
    },
};
// =============================================================================
// HELPER FUNCTIONS
// =============================================================================
function rollD20() {
    return Math.floor(Math.random() * 20) + 1;
}
/**
 * Roll the d20 with 5e advantage rules applied.
 *
 * Advantage keeps the higher of 2d20, disadvantage the lower, and holding both
 * at once cancels down to a flat 1d20 — they do not stack and they do not
 * accumulate, however many sources are in play.
 */
function rollD20Advantaged(wantAdvantage, wantDisadvantage) {
    const advantage = !!wantAdvantage && !wantDisadvantage;
    const disadvantage = !!wantDisadvantage && !wantAdvantage;
    const dice = advantage || disadvantage ? [rollD20(), rollD20()] : [rollD20()];
    const natural = advantage
        ? Math.max(...dice)
        : disadvantage
            ? Math.min(...dice)
            : dice[0];
    return { dice, natural, advantage, disadvantage };
}
/** `2d20kh1` / `2d20kl1` / `1d20`, plus the modifier if there is one. */
function d20Formula(roll, modifier) {
    let formula = roll.advantage ? '2d20kh1' : roll.disadvantage ? '2d20kl1' : '1d20';
    if (modifier !== 0) {
        formula += modifier > 0 ? ` + ${modifier}` : ` - ${Math.abs(modifier)}`;
    }
    return formula;
}
/** Conditions on the attacker that impose disadvantage on its attack rolls. */
const ATTACKER_DISADVANTAGE_CONDITIONS = new Set([
    'blinded', 'frightened', 'poisoned', 'prone', 'restrained',
]);
/** Conditions on the target that grant advantage to attacks against it. */
const TARGET_ADVANTAGE_CONDITIONS = new Set([
    'blinded', 'paralyzed', 'petrified', 'restrained', 'stunned', 'unconscious',
]);
/** Conditions on the target that impose disadvantage on attacks against it. */
const TARGET_DISADVANTAGE_CONDITIONS = new Set(['invisible']);
function hasCondition(combatant, ids) {
    return (combatant.character?.conditions ?? []).some(c => ids.has(c.conditionId));
}
/**
 * Work out whether an attack is made with advantage or disadvantage.
 *
 * Explicit `meta.advantage` / `meta.disadvantage` on the attacker wins — that
 * is the caller stating a circumstance the adapter cannot see (flanking, help,
 * cover, a class feature). Otherwise it is read off the conditions in play.
 *
 * Prone is treated as a melee attack against the target, which is the common
 * case in this engine; a ranged attacker against a prone target should pass
 * `meta.disadvantage` explicitly.
 */
function attackAdvantage(attacker, target) {
    const explicitAdv = attacker.meta?.advantage;
    const explicitDis = attacker.meta?.disadvantage;
    if (typeof explicitAdv === 'boolean' || typeof explicitDis === 'boolean') {
        return { advantage: !!explicitAdv, disadvantage: !!explicitDis };
    }
    const advantage = hasCondition(target, TARGET_ADVANTAGE_CONDITIONS) ||
        hasCondition(target, new Set(['prone'])) ||
        hasCondition(attacker, new Set(['invisible']));
    const disadvantage = hasCondition(attacker, ATTACKER_DISADVANTAGE_CONDITIONS) ||
        hasCondition(target, TARGET_DISADVANTAGE_CONDITIONS);
    return { advantage, disadvantage };
}
function rollDice(count, sides) {
    return Array.from({ length: count }, () => Math.floor(Math.random() * sides) + 1);
}
function getProficiencyBonus(level) {
    return Math.ceil(1 + level / 4);
}
function getHpStatus(current, max) {
    if (current <= 0)
        return 'unconscious';
    const ratio = current / max;
    if (ratio > 0.75)
        return 'healthy';
    if (ratio > 0.5)
        return 'wounded';
    if (ratio > 0.25)
        return 'bloodied';
    return 'critical';
}
// =============================================================================
// THE ADAPTER
// =============================================================================
export const dnd5eAdapter = {
    id: 'dnd-5e',
    name: 'D&D 5th Edition',
    version: '0.1.0',
    rules: DND5E_RULES,
    // --- Character Lifecycle ---
    createCharacter(options) {
        const abilities = options.abilities ?? {
            str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10,
        };
        const level = options.level ?? 1;
        const classId = options.classId ?? 'fighter';
        const className = ALL_CLASS_NAMES.find(c => c.toLowerCase() === classId) ?? 'Fighter';
        const hitDie = CLASS_HIT_DIE[className] ?? 8;
        const conMod = getModifier(abilities.con ?? 10);
        const maxHp = hitDie + conMod;
        return {
            id: crypto.randomUUID(),
            name: options.name,
            systemId: 'dnd-5e',
            abilities,
            level,
            classes: [{ classId, level, subclass: undefined }],
            health: { current: maxHp, max: maxHp, temp: 0 },
            defense: 10 + getModifier(abilities.dex ?? 10), // base AC = 10 + DEX mod
            speed: 30,
            conditions: [],
            proficiencies: {
                savingThrows: CLASS_SAVE_PROFICIENCIES[className] ?? [],
                skills: [],
            },
            magic: CLASS_CASTER_TYPE[className] !== 'none' ? {
                casterType: CLASS_CASTER_TYPE[className],
                slots: getSpellSlots(className, level),
            } : undefined,
        };
    },
    validateCharacter(character) {
        const errors = [];
        const warnings = [];
        if (!character.abilities || Object.keys(character.abilities).length === 0) {
            errors.push('Character must have ability scores');
        }
        for (const [ability, score] of Object.entries(character.abilities)) {
            if (score < 1 || score > 30) {
                errors.push(`${ability}: score ${score} out of range (1-30)`);
            }
        }
        if (character.level !== undefined) {
            if (character.level < 1 || character.level > 20) {
                errors.push(`Level ${character.level} out of range (1-20)`);
            }
        }
        if (character.classes) {
            const totalLevel = character.classes.reduce((sum, c) => sum + c.level, 0);
            if (totalLevel > 20) {
                errors.push(`Total class levels (${totalLevel}) exceed maximum (20)`);
            }
        }
        if (character.health.max <= 0) {
            errors.push('Max HP must be positive');
        }
        return { valid: errors.length === 0, errors, warnings };
    },
    getAbilityModifier(score) {
        return getModifier(score);
    },
    getAbilities() {
        return DND5E_ABILITIES;
    },
    getClasses() {
        return DND5E_CLASSES;
    },
    getConditions() {
        return DND5E_CONDITIONS;
    },
    levelUp(character, choices) {
        const updated = { ...character };
        const classId = choices.classId;
        const className = ALL_CLASS_NAMES.find(c => c.toLowerCase() === classId) ?? 'Fighter';
        const hitDie = CLASS_HIT_DIE[className] ?? 8;
        const conMod = getModifier(character.abilities.con ?? 10);
        // Increment class level
        const classes = [...(updated.classes ?? [])];
        const existing = classes.find(c => c.classId === classId);
        if (existing) {
            existing.level += 1;
        }
        else {
            classes.push({ classId, level: 1, subclass: choices.subclass });
        }
        updated.classes = classes;
        // Update total level
        const newLevel = classes.reduce((sum, c) => sum + c.level, 0);
        updated.level = newLevel;
        // HP: average + CON mod
        const hpGain = Math.max(1, Math.ceil(hitDie / 2) + 1 + conMod);
        updated.health = {
            ...updated.health,
            max: updated.health.max + hpGain,
            current: updated.health.current + hpGain,
        };
        // Apply ASI
        if (choices.abilityScoreImprovements) {
            const newAbilities = { ...updated.abilities };
            for (const [ability, increase] of Object.entries(choices.abilityScoreImprovements)) {
                newAbilities[ability] = Math.min(20, (newAbilities[ability] ?? 10) + increase);
            }
            updated.abilities = newAbilities;
        }
        return updated;
    },
    getProficiencyBonus(level) {
        return getProficiencyBonus(level);
    },
    // --- Dice & Resolution ---
    rollCheck(context) {
        const modifier = context.modifier ?? 0;
        const roll = rollD20Advantaged(context.advantage, context.disadvantage);
        const total = roll.natural + modifier;
        return {
            total,
            dice: roll.dice,
            modifier,
            formula: d20Formula(roll, modifier),
            natural: roll.natural,
            meta: {
                type: context.type,
                ability: context.ability,
                skill: context.skill,
                dc: context.dc,
                // Normalized, not echoed: asking for both cancels to a flat d20 and the
                // meta must say so, or the log disagrees with the dice.
                advantage: roll.advantage,
                disadvantage: roll.disadvantage,
            },
        };
    },
    interpretResult(roll, context) {
        const dc = context.dc ?? 10;
        if (roll.natural === 20) {
            return {
                outcome: 'critical-success',
                description: `Natural 20! Critical success (total ${roll.total} vs DC ${dc})`,
            };
        }
        if (roll.natural === 1) {
            return {
                outcome: 'critical-failure',
                description: `Natural 1! Critical failure (total ${roll.total} vs DC ${dc})`,
            };
        }
        if (roll.total >= dc) {
            return {
                outcome: 'success',
                description: `Success (${roll.total} vs DC ${dc})`,
            };
        }
        return {
            outcome: 'failure',
            description: `Failure (${roll.total} vs DC ${dc})`,
        };
    },
    // --- Combat ---
    combat: {
        rollInitiative(combatant) {
            const dexMod = combatant.character
                ? getModifier(combatant.character.abilities.dex ?? 10)
                : 0;
            const natural = rollD20();
            return natural + dexMod;
        },
        getActionEconomy(_combatant) {
            return {
                actions: [
                    { typeId: 'action', remaining: 1, options: ['Attack', 'Cast a Spell', 'Dash', 'Disengage', 'Dodge', 'Help', 'Hide', 'Ready', 'Search', 'Use an Object'] },
                    { typeId: 'bonus_action', remaining: 1, options: [] }, // class-specific
                    { typeId: 'reaction', remaining: 1, options: ['Opportunity Attack'] },
                ],
            };
        },
        resolveAttack(attacker, target, _action) {
            const atkMod = attacker.character
                ? getModifier(attacker.character.abilities.str ?? 10)
                : 0;
            const { advantage, disadvantage } = attackAdvantage(attacker, target);
            const d20 = rollD20Advantaged(advantage, disadvantage);
            const natural = d20.natural;
            const total = natural + atkMod;
            const targetAC = target.character?.defense ?? 10;
            const hit = natural === 20 || (natural !== 1 && total >= targetAC);
            const isCrit = natural === 20;
            const roll = {
                total, dice: d20.dice, modifier: atkMod,
                formula: d20Formula(d20, atkMod), natural,
                meta: { advantage: d20.advantage, disadvantage: d20.disadvantage },
            };
            const swing = d20.advantage ? ' with advantage' : d20.disadvantage ? ' with disadvantage' : '';
            const interpretation = {
                outcome: isCrit ? 'critical-success' : hit ? 'success' : 'failure',
                description: hit
                    ? `Hit!${swing} (${total} vs AC ${targetAC}${isCrit ? ' — Critical!' : ''})`
                    : `Miss${swing} (${total} vs AC ${targetAC})`,
            };
            // Damage (simplified: 1d8 + STR for melee)
            const damage = hit ? {
                total: rollDice(isCrit ? 2 : 1, 8).reduce((a, b) => a + b, 0) + atkMod,
                type: 'slashing',
                dice: isCrit ? '2d8' : '1d8',
            } : undefined;
            return { hit, roll, interpretation, damage };
        },
        applyDamage(target, damage) {
            const char = target.character;
            if (!char) {
                return { previousHp: 0, currentHp: 0, status: 'dead', effects: [] };
            }
            const previousHp = char.health.current;
            const newHp = Math.max(0, previousHp - damage.total);
            char.health.current = newHp;
            const effects = [];
            if (newHp === 0 && previousHp > 0) {
                effects.push('death-saves');
                if (damage.total >= char.health.max) {
                    effects.push('massive-damage');
                }
            }
            return {
                previousHp,
                currentHp: newHp,
                status: getHpStatus(newHp, char.health.max),
                effects,
            };
        },
    },
    // --- Magic ---
    magic: {
        getSpellSlots(character) {
            if (!character.classes || !character.magic) {
                return {};
            }
            const primaryClass = character.classes[0];
            if (!primaryClass)
                return {};
            const className = ALL_CLASS_NAMES.find(c => c.toLowerCase() === primaryClass.classId);
            if (!className)
                return {};
            const rawSlots = getSpellSlots(className, primaryClass.level);
            const slots = {};
            for (const [levelStr, count] of Object.entries(rawSlots)) {
                const level = parseInt(levelStr, 10);
                if (!isNaN(level) && count > 0) {
                    slots[level] = { total: count, used: 0 };
                }
            }
            return { slots };
        },
        validateCast(character, _spellName, level) {
            if (!character.magic) {
                return { canCast: false, reason: 'Character has no spellcasting ability' };
            }
            if (level === 0) {
                return { canCast: true }; // Cantrips always work
            }
            // Check if slot available (simplified)
            return { canCast: true, warnings: [] };
        },
        resolveCast(_character, spellName, _targets) {
            return {
                success: true,
                effects: [`Cast ${spellName}`],
            };
        },
    },
    // --- Encounter Balance ---
    balance: {
        rateEncounter(party, enemies) {
            const partyLevel = party.reduce((sum, c) => sum + (c.level ?? 1), 0) / party.length;
            const enemyCount = enemies.length;
            // Simplified CR-based rating
            const ratio = enemyCount / party.length;
            if (ratio <= 0.5)
                return { label: 'Easy', numericValue: 1, description: `${enemyCount} enemies vs ${party.length} party members (avg level ${partyLevel.toFixed(0)})` };
            if (ratio <= 1)
                return { label: 'Medium', numericValue: 2, description: `Balanced encounter` };
            if (ratio <= 1.5)
                return { label: 'Hard', numericValue: 3, description: `Challenging encounter` };
            return { label: 'Deadly', numericValue: 4, description: `Potentially lethal encounter`, warnings: ['Consider providing escape routes'] };
        },
        suggestEncounter(_party, _difficulty) {
            return []; // Would query bestiary
        },
    },
    // --- DM Guidance ---
    getDmGuidance() {
        return DND5E_RULES.dmGuidance;
    },
};
//# sourceMappingURL=dnd5e-adapter.js.map